import {
  DEFAULT_GP_TEMPLATE_IDS,
  PREBUILT_TEMPLATES,
  TemplateType,
} from "../constants/templateData";
import { SPECIALTIES } from "../constants/doctorData";
import type { Specialty } from "../types/doctorTypes";
import type { TemplateObject } from "../types/templateTypes";

const validateSpecialty = (specialty: string) => {
  if (typeof specialty !== "string") {
    return false;
  }
  return Object.values(SPECIALTIES).includes(specialty as Specialty);
};

export const getAllTemplatesBySpecialty = (
  specialty: string
): TemplateObject[] => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }

  const specialtyTemplates =
    specialty !== SPECIALTIES.GENERAL_PRACTICE
      ? PREBUILT_TEMPLATES[specialty as Specialty] ?? {}
      : {};

  const generalPracticeTemplates =
    PREBUILT_TEMPLATES[SPECIALTIES.GENERAL_PRACTICE] ?? {};
  return [
    ...Object.values(specialtyTemplates).flat(),
    ...Object.values(generalPracticeTemplates).flat(),
  ] as TemplateObject[];
};

export const getTemplatesByType = (
  specialty: string,
  type: TemplateType
): TemplateObject[] => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }
  return [
    ...(PREBUILT_TEMPLATES[specialty as Specialty]?.[type] || []),
    ...(specialty !== SPECIALTIES.GENERAL_PRACTICE ? (PREBUILT_TEMPLATES[SPECIALTIES.GENERAL_PRACTICE]?.[type] || []) : []),
  ] as TemplateObject[];
};

export const getAllPrebuiltTemplateIds = (
  specialty: string
): string[] => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }
  return getAllTemplatesBySpecialty(specialty).map((template) => template.id);
};

// first template in specialty tempaltes is the default
export const getDefaultPrebuiltTemplateId = (
  specialty: string,
  type: TemplateType
): string => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }
  return (
    getAllTemplatesBySpecialty(specialty).find(
      (template) => template.type === type
    )?.id ?? DEFAULT_GP_TEMPLATE_IDS[type]
  );
};

export const getDefaultPrebuiltTemplateIds = (
  specialty: string
): string[] => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }

  const allTemplates = getAllTemplatesBySpecialty(specialty);
  const found = new Set<string>();

  const filtered = allTemplates.reduce<string[]>((acc, template) => {
    if (!template.type || found.has(template.type)) return acc;
    found.add(template.type);
    acc.push(template.id);
    return acc;
  }, []);
  return filtered;
};

export const sortTemplates = ({
  templates,
  specialty,
  emailAddress,
  defaults,
}: {
  templates: TemplateObject[];
  specialty: string;
  emailAddress: string;
  defaults: string[];
}) => {
  if (!validateSpecialty(specialty)) {
    specialty = SPECIALTIES.GENERAL_PRACTICE;
  }
  const typeOrder = [
    TemplateType.MEDICAL,
    TemplateType.RECORDSRECAP,
    TemplateType.COMMUNICATIONS,
  ];
  const defaultTemplateIdsSet = new Set(defaults);
  
  return [...templates].sort((a, b) => {
    // favorited
    if (defaultTemplateIdsSet.has(b.id) && !defaultTemplateIdsSet.has(a.id))
      return 1;
    if (defaultTemplateIdsSet.has(a.id) && !defaultTemplateIdsSet.has(b.id))
      return -1;
    // owner
    if (b.owner === emailAddress && a.owner !== emailAddress) return 1;
    if (a.owner === emailAddress && b.owner !== emailAddress) return -1;
    // specialty
    if (
      b.tag?.includes(specialty) &&
      !a.tag?.includes(specialty)
    )
      return 1;
    if (
      a.tag?.includes(specialty) &&
      !b.tag?.includes(specialty)
    )
      return -1;
    // type order
    const aIndex = typeOrder.indexOf(a.type as TemplateType);
    const bIndex = typeOrder.indexOf(b.type as TemplateType);
    if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) return aIndex - bIndex;
    // alphabetical
    return a.name.localeCompare(b.name);
  });
};
